import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  isAxiosError,
} from 'axios';
import * as Sentry from '@sentry/react';
import { updateTokens, logout } from 'store/actions/auth.actions';
import { clearProfileState } from 'store/actions/profile.actions';
import { API_METHODS } from 'config/constants';
import { ErrorAlert } from 'components/Patient/context/ErrorContext';
import { ErrorField } from 'entities/Patient/Patient/dto/responseTypes';
import {
  reportApiError,
  reportApiResponse,
} from 'utils/diagnostics/apiDiagnostics';

const ERROR_TOKEN = Symbol('ERROR_TOKEN');
const CANCELLATION_TOKEN = Symbol('CANCELLATION_TOKEN');
type TokenResult = string | typeof ERROR_TOKEN | typeof CANCELLATION_TOKEN;

let axiosClient: AxiosClient;

export type AxiosClientProps = {
  readonly API_URL: string;
  readonly API_URL_FOR_LISTS: string;
  readonly DMS_API_URL?: string;
  readonly store: any;
  readonly setErrorMessageAlert?: (messages: ErrorAlert | null) => void;
};

class AxiosClient {
  _errorSubscribers: Array<(messages: ErrorAlert | null) => void> = [];
  _API_URL: string;
  _API_URL_FOR_LISTS: string;
  _DMS_API_URL: string;
  _store: any; // Redux store
  _client: AxiosInstance;
  _altClient: AxiosInstance;
  _dmsClient: AxiosInstance;
  _refreshClient: AxiosInstance;
  tokenSubscribers: Array<(result: TokenResult) => void> = [];
  isAlreadyFetchingAccessToken = false;
  hasShownUnauthorizedAlert = false;
  postPermissionChangeUntil: number | null = null;
  retriedAfter403Configs: WeakSet<InternalAxiosRequestConfig<any>> =
    new WeakSet();

  constructor(props: AxiosClientProps) {
    Object.keys(props).forEach((propName) => {
      // @ts-ignore
      this[`_${propName}`] = props[propName];
    });

    this._API_URL = props.API_URL;
    this._API_URL_FOR_LISTS = props.API_URL_FOR_LISTS;
    this._DMS_API_URL = props.DMS_API_URL || '/dms-api';
    if (props.setErrorMessageAlert) {
      this._errorSubscribers.push(props.setErrorMessageAlert);
    }

    const localAxios = axios.create({
      baseURL: this._API_URL,
    });

    const altAxios = axios.create({
      baseURL: this._API_URL_FOR_LISTS,
    });

    const dmsAxios = axios.create({
      baseURL: this._DMS_API_URL,
    });

    const refreshAxios = axios.create({
      baseURL: this._API_URL,
    });

    localAxios.interceptors.request.use(this.onInterceptorsRequest);
    altAxios.interceptors.request.use(this.onInterceptorsRequest);
    dmsAxios.interceptors.request.use(this.onInterceptorsRequest);

    localAxios.interceptors.response.use(...this.onInterceptorsResponse());
    altAxios.interceptors.response.use(...this.onInterceptorsResponse());
    dmsAxios.interceptors.response.use(...this.onInterceptorsResponse());

    this._client = localAxios;
    this._altClient = altAxios;
    this._dmsClient = dmsAxios;
    this._refreshClient = refreshAxios;
  }

  onInterceptorsRequest = (
    config: InternalAxiosRequestConfig<any>,
  ): InternalAxiosRequestConfig<any> => {
    const { authState = {} } = this._store.getState();
    const { accessToken = '' } = authState;

    config.headers['Accept'] = 'application/json';
    config.headers['Content-Type'] = 'application/json';
    config.headers['Access-Control-Allow-Origin'] = '*';

    const shouldAttachAuth = accessToken && !config.headers.Authorization;
    const hasAnyAuth = !!config.headers.Authorization || !!accessToken;

    if (shouldAttachAuth) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Gate outgoing authorized requests while a refresh is in-flight to avoid sending stale tokens
    if (this.isAlreadyFetchingAccessToken && hasAnyAuth) {
      return new Promise((resolve) => {
        this.awaitRefreshToken()
          .then((newAccessToken) => {
            config.headers['Authorization'] = `Bearer ${newAccessToken}`;
            resolve(config);
          })
          .catch(() => {
            // If refresh failed, proceed with existing config; downstream will handle 401/403
            resolve(config);
          });
      }) as unknown as InternalAxiosRequestConfig<any>;
    }

    return config;
  };

  onInterceptorsResponse = () => {
    /**
     * @todo: This is bad. Despite cutting down data.data calls, it's stripping all data from the
     * request, and confusing the types. {@link https://github.com/axios/axios/issues/1510}
     */
    const returnResponseData = (response: AxiosResponse) => {
      // A 200 carrying the SPA index.html means the API path is not proxied to a backend.
      reportApiResponse(response);
      // Preserve the headers in the data object
      const responseData =
        response.data && typeof response.data === 'object'
          ? response.data
          : { data: response.data };
      responseData.headers = response.headers;
      return responseData;
    };
    const returnResponseError = async (error: any) => {
      const { response = {} } = error;
      const requestConfig = isAxiosError(error) ? error.config : undefined;

      reportApiError(error);

      const isAuthorizedRequestFailed =
        response.status === 401 && response.config.headers.Authorization;
      const { authState } = this._store.getState();

      // For 401s with an Authorization header, attempt refresh first and only show an alert if refresh fails.
      // For non-401 errors, show the alert immediately (unless disabled) and return the error payload.
      if (!isAuthorizedRequestFailed) {
        // Handle a potential stale-claims 403 within a short guard window after permission changes
        const isForbidden = response?.status === 403;
        const hadAuthHeader = !!response?.config?.headers?.Authorization;
        if (
          isForbidden &&
          hadAuthHeader &&
          this.isWithinPostPermissionChangeWindow() &&
          requestConfig &&
          !this.retriedAfter403Configs.has(
            requestConfig as InternalAxiosRequestConfig<any>,
          )
        ) {
          try {
            this.retriedAfter403Configs.add(
              requestConfig as InternalAxiosRequestConfig<any>,
            );
            const latestAccessToken = await this.awaitRefreshToken();
            requestConfig.headers = requestConfig.headers || {};
            requestConfig.headers.Authorization = 'Bearer ' + latestAccessToken;
            return this._client.request(requestConfig);
          } catch (e) {
            // fall through to normal handling
          }
        }

        if (
          !requestConfig?.disabledErrorAlert &&
          this._errorSubscribers.length > 0 &&
          !this.hasShownUnauthorizedAlert &&
          !!authState.isAuth
        ) {
          const errors = Array.isArray(response?.data?.errors)
            ? response?.data?.errors?.map(
                (error: ErrorField) => error.message ?? '',
              )
            : [
                'Something went wrong processing the request. Please try again later.',
              ];

          const errorMsg = {
            title: response?.data?.message ?? 'Unexpected error occurred',
            text: errors,
          };

          this.notifyErrorSubscribers(errorMsg);
          this.hasShownUnauthorizedAlert = true;
        }

        return {
          statusCode: response?.status,
          ...response?.data,
        };
      }

      this.refreshToken();

      const { config: originalRequest = {} } = error;

      return new Promise((resolve) => {
        this.addSubscriber((result) => {
          if (result === ERROR_TOKEN) {
            resolve({
              statusCode: 401,
              code: 'auth.token_refresh_failed',
              message: 'Access token refresh failed. The user is logged out.',
              errors: [
                {
                  message:
                    'Access token refresh failed. The user is logged out.',
                },
              ],
            });

            this.handleTokenRefreshFailure();
          } else if (result === CANCELLATION_TOKEN) {
            resolve({
              statusCode: 401,
              code: 'auth.token_refresh_cancelled',
              message: 'Token refresh was cancelled.',
            });
          } else {
            originalRequest.headers.Authorization = 'Bearer ' + result;
            resolve(this._client.request(originalRequest));
          }
        });
      });
    };

    return [returnResponseData, returnResponseError];
  };

  onAccessTokenFetched(accessToken: string) {
    this.tokenSubscribers = this.tokenSubscribers.filter((callback) =>
      callback(accessToken),
    );
    this.hasShownUnauthorizedAlert = false;
    this.notifyErrorSubscribers(null);
  }

  onAccessTokenError() {
    this.tokenSubscribers = this.tokenSubscribers.filter((callback) =>
      callback(ERROR_TOKEN),
    );
  }

  addSubscriber(callback: (result: TokenResult) => void) {
    this.tokenSubscribers.push(callback);
  }

  /**
   * Internal: Opens a short guard window after permission changes.
   *
   * During this window the response interceptor treats a 403 from endpoints requiring
   * newly granted permissions as a potential "stale-claims" symptom and will perform
   * a one-time refresh+retry of the original request. Outside this window, 403s are
   * surfaced as normal permission errors with no retry.
   *
   * Notes
   * - The window is intentionally short (default 10s) to bridge backend blacklist cache TTLs.
   * - This method is private by design; callers should use refreshAfterPermissionChange().
   */
  private setPostPermissionChangeGuard() {
    this.postPermissionChangeUntil = Date.now() + 10000;
  }

  private isWithinPostPermissionChangeWindow(): boolean {
    return (
      typeof this.postPermissionChangeUntil === 'number' &&
      Date.now() < (this.postPermissionChangeUntil as number)
    );
  }

  public awaitRefreshToken(): Promise<string> {
    if (!this.isAlreadyFetchingAccessToken) {
      this.refreshToken();
    }
    return new Promise<string>((resolve, reject) => {
      this.addSubscriber((result) => {
        if (result === ERROR_TOKEN) {
          reject(new Error('auth.token_refresh_failed'));
        } else if (result === CANCELLATION_TOKEN) {
          // Silently resolve on cancellation
          resolve('');
        } else {
          resolve(result);
        }
      });
    });
  }

  /**
   * Convenience method to eliminate the stale-token window after permission changes.
   *
   * Purpose
   * - Immediately after actions that change permissions (e.g., registration submit,
   *   group/role updates), ensure all follow-up requests carry the updated claims.
   *
   * What it does
   * 1) Sets a short internal guard window so that any 403s observed during backend
   *    propagation are treated as potentially stale and retried once after refresh.
   * 2) Triggers a single-flight access-token refresh (if not already in-flight) and
   *    awaits its completion so subsequent protected requests use the fresh token.
   *
   * Behavior
   * - While refresh is in-flight, outgoing authorized requests are transparently queued
   *   and will resume with the new token (see onInterceptorsRequest).
   * - Only one refresh call is performed for a burst; all waiters are resolved via
   *   tokenSubscribers when the new token arrives.
   *
   * Returns
   * - Promise<void> resolving when a fresh access token is ready.
   */
  public refreshAfterPermissionChange(): Promise<void> {
    this.setPostPermissionChangeGuard();
    return this.awaitRefreshToken().then(() => {});
  }

  private handleTokenRefreshFailure = () => {
    Sentry.captureMessage('Token refresh failed - user will be logged out', {
      level: 'warning',
      tags: {
        errorType: 'token_refresh_failure',
        component: 'AxiosClient',
      },
      contexts: {
        auth: {
          isAlreadyFetchingAccessToken: this.isAlreadyFetchingAccessToken,
          hasShownUnauthorizedAlert: this.hasShownUnauthorizedAlert,
        },
      },
    });

    this.performLogout();
  };

  public clearErrorMessages() {
    this.notifyErrorSubscribers(null);
    this.hasShownUnauthorizedAlert = false;
  }

  private resetInternalState() {
    this.hasShownUnauthorizedAlert = false;
    this.isAlreadyFetchingAccessToken = false;

    this.tokenSubscribers.forEach((callback) => callback(CANCELLATION_TOKEN));
    this.tokenSubscribers = [];

    this.postPermissionChangeUntil = null;
    this.retriedAfter403Configs = new WeakSet();
  }

  private clearErrorAlerts() {
    this.notifyErrorSubscribers(null);
  }

  public performLogout() {
    this.resetInternalState();
    this.clearErrorAlerts();

    this._store.dispatch(logout());
    this._store.dispatch(clearProfileState('personnelOrganizations'));
  }

  refreshToken() {
    if (!this.isAlreadyFetchingAccessToken) {
      this.isAlreadyFetchingAccessToken = true;

      const { authState = {} } = this._store.getState();
      const { refreshToken = '' } = authState;

      this._refreshClient
        .put(`${API_METHODS.AUTH_REFRESH}?token=${refreshToken}`)
        .then(
          (refreshResponse) => {
            const { data = {} } = refreshResponse.data;
            const { accessToken: newAccessToken } = data;
            this.isAlreadyFetchingAccessToken = false;
            this.onAccessTokenFetched(newAccessToken);
            this._store.dispatch(updateTokens(data));
          },
          (refreshError) => {
            const { response: refreshErrorResponse = {} } = refreshError;
            if (refreshErrorResponse.status !== 200) {
              this.isAlreadyFetchingAccessToken = false;
              this.onAccessTokenError();
            } else {
              setTimeout(() => this.refreshToken(), 1500);
            }
          },
        );
    }
  }

  getAxios() {
    return this._client;
  }

  getListAxios() {
    return this._altClient;
  }

  getDmsAxios() {
    return this._dmsClient;
  }

  notifyErrorSubscribers(messages: ErrorAlert | null) {
    this._errorSubscribers.forEach((subscriber) => subscriber(messages));
  }

  subscribeToErrors(subscriber: (messages: ErrorAlert | null) => void) {
    this._errorSubscribers.push(subscriber);
    return () => {
      this._errorSubscribers = this._errorSubscribers.filter(
        (s) => s !== subscriber,
      );
    };
  }
}

/*
 * Initialization
 * @param {object} props - some properties
 */
function init(props: AxiosClientProps) {
  axiosClient = new AxiosClient(props);
}

/*
 * Will return new instance of Axios
 * @returns {object}
 */
function getAxios() {
  return axiosClient.getAxios();
}

function getAxiosWrapper() {
  return axiosClient;
}

function getListAxios() {
  return axiosClient.getListAxios();
}

function getDmsAxios() {
  return axiosClient.getDmsAxios();
}

function subscribeToErrors(subscriber: (messages: ErrorAlert | null) => void) {
  return axiosClient.subscribeToErrors(subscriber);
}

export {
  init,
  getAxios,
  getListAxios,
  getDmsAxios,
  getAxiosWrapper,
  subscribeToErrors,
};
